import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import LoadingButton from "./LoadingButton";
import ErrorDisplay from "./ErrorDisplay";
import {
  TraceabilityIssues,
  parseTraceabilityIssues,
} from "../../traceabilityIssues";
import { useTokenOps } from "../../../components/TokenContext";
import { hostname } from "../../../providers/api";
import {
  UPDATE_VERSION_MODES,
  getVersionSetOptions,
} from "../traceabilityVersionUpdate";
import "./TraceabilitySection.css";

// Normalizes the version-targets response into a plain array of version strings.
// Backend contract (IT-2866): { sourceVersion, targetVersions: [...], latestVersion, isSupported }.
// Still tolerates a bare array / alternative keys in case the contract evolves.
const parseVersionTargets = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.targetVersions || data?.targets || data?.versions || data?.data || [];
  return list
    .map((item) => (typeof item === "string" ? item : item?.version || item?.name))
    .filter(Boolean);
};

const TraceabilitySection = ({
  validateExcel,
  refineTraceability,
  updateVersion,
  downloadTraceability,
  currentVersion,
  bcomeVersionRecordMap,
  errors,
  warnings,
  uploadedName,
  loading,
  loadingRefine,
  loadingUpdateVersion,
  loadingDownload,
}) => {
  const tokenOps = useTokenOps();
  // Same grouped tables as the File uploads detail; anything unrecognised falls back to ErrorDisplay
  const errorIssues = parseTraceabilityIssues(errors?.error ?? errors);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [mode, setMode] = useState(UPDATE_VERSION_MODES.DOWNLOAD);
  const [targetVersion, setTargetVersion] = useState("");
  const [bcomeVersionId, setBcomeVersionId] = useState("");
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState(null);

  const isAutoUpload = mode === UPDATE_VERSION_MODES.AUTO_UPLOAD;
  const versionSetOptions = useMemo(
    () => getVersionSetOptions(bcomeVersionRecordMap, targetVersion),
    [bcomeVersionRecordMap, targetVersion]
  );

  // Preselect the Version Set when the target version has exactly one; otherwise the
  // user must pick one. Drops a selection that no longer matches the target version.
  React.useEffect(() => {
    if (versionSetOptions.length === 1) {
      setBcomeVersionId(versionSetOptions[0].id);
    } else if (!versionSetOptions.some((option) => option.id === bcomeVersionId)) {
      setBcomeVersionId("");
    }
  }, [versionSetOptions, bcomeVersionId]);

  // Fetches the versions a project can be updated to, given its current version.
  // Backend contract (IT-2866): GET /projects/traceability-version-targets?sourceVersion=<current>
  const fetchVersionTargets = () => {
    setLoadingVersions(true);
    setVersionsError(null);
    setVersions([]);
    const query = currentVersion
      ? `?sourceVersion=${encodeURIComponent(currentVersion)}`
      : "";
    fetch(`${hostname}/projects/traceability-version-targets${query}`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then(async (response) => {
        if (response.status >= 400) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(
            errData.message || errData.error || "Could not load available versions"
          );
        }
        return response.json();
      })
      .then((data) => {
        const list = parseVersionTargets(data);
        setVersions(list);
        // Default to the backend's latestVersion when it's a valid target,
        // otherwise fall back to the last available target.
        const preferred =
          data?.latestVersion && list.includes(data.latestVersion)
            ? data.latestVersion
            : list.length
            ? list[list.length - 1]
            : "";
        setTargetVersion(preferred);
      })
      .catch((error) => {
        setVersions([]);
        setVersionsError(error.message || "Could not load available versions");
      })
      .finally(() => setLoadingVersions(false));
  };

  const openVersionModal = () => {
    setMode(UPDATE_VERSION_MODES.DOWNLOAD);
    setBcomeVersionId("");
    setVersionModalOpen(true);
    fetchVersionTargets();
  };

  const closeVersionModal = () => setVersionModalOpen(false);

  const handleUpdate = () => {
    updateVersion(
      targetVersion,
      isAutoUpload ? { autoUpload: true, bcomeVersionId } : {}
    );
    setVersionModalOpen(false);
  };

  const updateDisabled =
    loadingVersions || !targetVersion || (isAutoUpload && !bcomeVersionId);

  return (
    <div className="traceability-section">
      <h3 className="traceability-section__title">Traceability</h3>
      <div className="traceability-section__buttons">
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={validateExcel('simulateUploadTraceability')}
          loading={loading}
        >
          Check
        </LoadingButton>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={refineTraceability}
          loading={loadingRefine}
        >
          Refinement
        </LoadingButton>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={openVersionModal}
          loading={loadingUpdateVersion}
        >
          Update Version
        </LoadingButton>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={downloadTraceability}
          loading={loadingDownload}
        >
          Download
        </LoadingButton>
      </div>
      {errorIssues.length === 0 && <ErrorDisplay errors={errors} />}
      <TraceabilityIssues
        errors={errorIssues}
        warnings={warnings}
        uploadedName={uploadedName}
      />
      {errorIssues.length > 0 && errors?.note && (
        <p className="traceability-section__note">{errors.note}</p>
      )}

      <Dialog
        open={versionModalOpen}
        onClose={closeVersionModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Select Version</DialogTitle>
        <DialogContent>
          <RadioGroup
            className="traceability-section__modes"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <FormControlLabel
              value={UPDATE_VERSION_MODES.DOWNLOAD}
              control={<Radio color="primary" size="small" />}
              label="Download Excel"
            />
            <FormControlLabel
              value={UPDATE_VERSION_MODES.AUTO_UPLOAD}
              control={<Radio color="primary" size="small" />}
              label="Update and upload automatically"
            />
          </RadioGroup>
          <p className="traceability-section__current-version">
            Project current version:{" "}
            <strong>{currentVersion || "—"}</strong>
          </p>
          {loadingVersions ? (
            <div className="traceability-section__versions-loading">
              <CircularProgress size={24} />
            </div>
          ) : versionsError ? (
            <ErrorDisplay errors={versionsError} />
          ) : versions.length === 0 ? (
            <p className="traceability-section__no-versions">
              No target versions available for this project.
            </p>
          ) : (
            <TextField
              select
              label="Target version"
              value={targetVersion}
              onChange={(e) => setTargetVersion(e.target.value)}
              fullWidth
              variant="outlined"
              margin="dense"
            >
              {versions.map((version) => (
                <MenuItem key={version} value={version}>
                  {version}
                  {version === currentVersion ? " (current)" : ""}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isAutoUpload && !loadingVersions && targetVersion && (
            <>
              {versionSetOptions.length === 0 ? (
                <p className="traceability-section__no-versions">
                  No active Version Set for version {targetVersion}. Activate one
                  or use Download Excel.
                </p>
              ) : (
                <TextField
                  select
                  label="Version Set"
                  value={bcomeVersionId}
                  onChange={(e) => setBcomeVersionId(e.target.value)}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                >
                  {versionSetOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <p className="traceability-section__auto-upload-notice">
                The project&apos;s traceability data will be replaced by the updated
                Excel. Run calculation is not launched: run it manually once the
                upload succeeds. Progress is shown in Traceability status.
              </p>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVersionModal} disabled={loadingUpdateVersion}>
            Cancel
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            onClick={handleUpdate}
            loading={loadingUpdateVersion}
            disabled={updateDisabled}
          >
            {isAutoUpload ? "Update and upload" : "Update"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TraceabilitySection;
